import { Page, PageInner, PageHeader, PageSection, Breadcrumbs, BreadcrumbTemplateEnum, PageHeaderRight } from "@app/shared";
import { Unauthorized } from "@app/shared/ErrorPages";
import { Roles, UserContext } from "@app/user";
import { FC, useContext, useEffect, useState } from "react";
import AdequacyBreakdown from "./tiles/AdequacyBreakdown";
import ExceptionsTile from "./tiles/ExceptionsTile";
import GapOverview from "./tiles/GapOverview";
import GapsByAge from "./tiles/GapsByAge";
import TopSpecialtiesByGap from "./tiles/TopSpecialtiesByGap";
import TopCountiesByGap from "./tiles/TopCountiesByGap";
import EnterpriseComplianceDashboardContextProvider, { EnterpriseComplianceDashboardContext } from "./contexts/EcdContext";
import EnterpriseComplianceDashboardFilters from "./EcdFilters";
import CompositeFillableGaps from "./tiles/CompositeFillableGaps";
import LineOfBusinessOverview from "./tiles/LineOfBusinessOverview";
import CountyStatusOverview from "./tiles/CountyStatusOverview";
import EnterpriseComplianceFiltersContextProvider, { EnterpriseComplianceFiltersContext } from "./contexts/EcdFiltersContext";
import EcdColorsContextProvider, { EcdColorsContext } from "./contexts/EcdColorsContext";
import { QESAlert } from "@app/shared/qes-standards";
import { FlyoutLink } from "@app/shared/Flyouts";
import { EcdCustomizeColorsFlyout } from "./flyouts/EcdCustomizeColorsFlyout";
import { FeatureFlagContext, Features } from "@app/feature-flags";
import { Link } from "react-router-dom";
import { EcdReportGenerationRouteState } from "./EcdReportGenerationRouteState";

const WrappedEnterpriseComplianceDashboard: FC = () => {
    const user = useContext(UserContext);
    const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
    const dashboardContext = useContext(EnterpriseComplianceDashboardContext);
    const filtersContext = useContext(EnterpriseComplianceFiltersContext);
    const colorsContext = useContext(EcdColorsContext);
    const [ecdColorsFlyoutHasChanged, setEcdColorsFlyoutHasChanged] = useState(false);

    if (!user.roles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER)) {
        return <Unauthorized />;
    }
    const [isDesktopSized, setIsDesktopSized] = useState(window.matchMedia("(min-width: 1200px)").matches);

    useEffect(() => {
        const mql = window.matchMedia("(min-width: 1200px)");
        const resizeListener = (e: MediaQueryListEvent) => {
            setIsDesktopSized(e.matches);
        }
        mql.addEventListener("change", resizeListener);
    }, []);

    return (
        <Page documentTitle="Enterprise Compliance Dashboard">
            <Breadcrumbs template={BreadcrumbTemplateEnum.Overviews} items={["Enterprise Compliance Dashboard"]} />
            <PageInner>
                <PageHeader title="Enterprise Compliance Dashboard" disableHR={true}>
                    <PageHeaderRight>
                        {featureFlags.has(Features.ECD_COLOR_CUSTOMIZATION) && (
                          <FlyoutLink
                            content={{
                              body: <EcdCustomizeColorsFlyout onChange={(change: boolean) => setEcdColorsFlyoutHasChanged(change)} />,
                              title: "Customize Colors",
                            }}
                            classNames="qes-button-secondary"
                            testId="enterprise-compliance-dashboard__customize-colors"
                            warnOnClose={ecdColorsFlyoutHasChanged}
                            modalContent={{
                              body: "You will lose unsaved changes. Continue with closing the sidebar?",
                              cancelButtonText: "Cancel",
                              confirmButtonText: "Continue",
                              title: "Warning",
                            }}
                          >
                            Customize Colors
                          </FlyoutLink>
                         )}
                        {featureFlags.has(Features.ECD_REPORT_ENHANCEMENT) && dashboardContext.showExportButton && (
                          <Link<EcdReportGenerationRouteState> to={{
                            pathname: "/overviews/enterprise-compliance-dashboard/generate-report",
                            state: {
                              adequacyBreakdownData: dashboardContext.adequacyBreakdownData,
                              apiReadyFilters: filtersContext.apiReadyFilters,
                              arrowNegative: colorsContext.arrowNegative,
                              arrowPositive: colorsContext.arrowPositive,
                              compositeFillableGapsData: dashboardContext.compositeFillableGapsData,
                              countyStatusData: dashboardContext.countyStatusData,
                              defaultColors: colorsContext.defaultColors,
                              displayFilters: filtersContext.displayFilters,
                              exceptionsArray: colorsContext.exceptionsArray,
                              exceptionsData: dashboardContext.exceptionsData,
                              exceptionsPrimary: colorsContext.exceptionsPrimary,
                              exceptionsTimeFrame: filtersContext.exceptionsTimeFrame,
                              fillableGapsPrimary: colorsContext.fillableGapsPrimary,
                              fillableGapsSecondary: colorsContext.fillableGapsSecondary,
                              gapOverviewData: dashboardContext.gapOverviewData,
                              gapOverviewTimeFrame: filtersContext.gapOverviewTimeFrame,
                              gapsArray: colorsContext.gapsArray,
                              gapsByAgeData: dashboardContext.gapsByAgeData,
                              gapsByAgeTimeFrame: filtersContext.gapsByAgeTimeFrame,
                              gapsBySpecialtyData: dashboardContext.gapsBySpecialtyData,
                              gapsPrimary: colorsContext.gapsPrimary,
                              lineOfBusinessData: dashboardContext.lineOfBusinessData,
                              topCountiesByGapData: dashboardContext.topCountiesByGapData,
                              unfillableGaps: colorsContext.unfillableGaps
                            },
                          }}
                            className="qes-button qes-button-primary mt-1 mr5" type="button" data-testid="enterprise-compliance-dashboard__export">Export</Link>
                        )}
                    </PageHeaderRight>
                </PageHeader>
                <PageSection>
                    <EnterpriseComplianceDashboardFilters isDesktopSized={isDesktopSized} />
                    {dashboardContext.showNoResultsAlert && (
                        <QESAlert type="info" persist={true} classNames="mb-3">
                            There is no data for the current combination of applied filters.
                        </QESAlert>
                    )}
                    <div className="enterprise-compliance-dashboard__content row">
                        {/* Row 1 */}
                        <AdequacyBreakdown />
                        {isDesktopSized ? (
                            <>
                                <GapOverview />
                                <CompositeFillableGaps />
                            </>
                        ) : (
                            <>
                                <CompositeFillableGaps />
                                <GapOverview />
                            </>
                        )}
                        {/* Row 2 */}
                        <GapsByAge />
                        <ExceptionsTile />
                        {/* Row 3 */}
                        <TopSpecialtiesByGap />
                        <TopCountiesByGap />
                        {/* Row 4 */}
                        <LineOfBusinessOverview />
                        <CountyStatusOverview />
                    </div>
                </PageSection>
            </PageInner>
        </Page>
    );
};

const EnterpriseComplianceDashboard: FC = () => (
    <EnterpriseComplianceFiltersContextProvider>
        <EnterpriseComplianceDashboardContextProvider>
            <EcdColorsContextProvider>
                <WrappedEnterpriseComplianceDashboard />
            </EcdColorsContextProvider>
        </EnterpriseComplianceDashboardContextProvider>
    </EnterpriseComplianceFiltersContextProvider>
);

export default EnterpriseComplianceDashboard;
